import React, { useState, useEffect, useRef } from 'react'
import { Box, Typography, useMediaQuery } from '@material-ui/core'
import { ContainerWithCurve } from '../../styles/ContainerWithCurve'
import TypographyCMS, { createTypographyProps } from '../Typography/Typography'
import MediaImage from '../Media/MediaImage'
import classesOld from '../Packages/Packages.styles'
import theme, { BOX, COLORS } from '../../styles/theme'
import {
  getSpeed,
} from '../../utils/getProductDetails'
import { useOrderContext } from '../NewOrderFlow/OrderContext'
import { cx } from 'linaria'
import { PackageProps } from '../Packages/Package'
import Features from '../Packages/Features'
import Price from '../Packages/Price'
import { AnimationLine, AnimationLineRef } from '../Packages/AnimationLine'
import { useRouter } from 'next/router'
import { Flag } from '../Packages/Flag'
import {
  HobsProduct, ProductOffering,
} from '../../api/Packages'
import createCRMApiClient from '../../api/CRMApi'
import PackageCTA from '../Packages/PackageCTA'
import { withStyles } from '@material-ui/core/styles'
import { PackageStyles } from '../Packages/Packages.styles2'
import Wrapper from '../Wrapper/Wrapper'
import { addToCartOptions } from '../Packages/Helpers'
import { useGeneralContext } from '../GeneralContext/GeneralContext'
import orangeFlashIcon from '../../../public/orange-flash-sale-icon.svg'
import flashIcon from '../../../public/flash-sale-icon.svg'
import useIntersectionObserver from '../EnquiryBottomBar.js/EnquiryHook'
import { isPremiseLevelPricingDateValid } from '../../utils/premiseLevelPricingUtils'

export const HobsAddonsInBundleType = {
  VOICE: ['Calling_Product_bundle', 'CallingProductVoice'],
  WIER: ['Wier_Product_bundle', 'WierProductbundle'],
  NORTON: ['Norton_360_Protect', 'Norton360Protect'],
  TV: ['Fibre_TV_Product', 'FibreTVProduct'],
} as const

export type HobsAddonsInBundleTypeKey = keyof typeof HobsAddonsInBundleType

interface Bundle {
  productOffering?: ProductOffering[]
}

export const getAddonFromBundle = (
  bundle: Bundle, 
  type: HobsAddonsInBundleTypeKey,
): HobsProduct | null | false => {
  const products = bundle?.productOffering

  if (!products?.length) {
    return false
  }

  const ids = HobsAddonsInBundleType[type] as readonly string[]
  return products.find(
    (addon: HobsProduct) =>
      addon.productOfferingId &&
      typeof addon.productOfferingId === 'string' &&
      Array.isArray(ids) &&
      ids.includes(addon.productOfferingId)
  ) || null
}

export const checkIfAddonExists = (type: HobsAddonsInBundleTypeKey) => (bundle: PackageProps) => 
  getAddonFromBundle(bundle, type)

export const checkIfVoiceExists = checkIfAddonExists('VOICE')
export const checkIfWierExists = checkIfAddonExists('WIER')
export const checkIfVasExists = checkIfAddonExists('NORTON')
export const checkIfTVExists = checkIfAddonExists('TV')

const Bundle = withStyles(PackageStyles)(({
  selected,
  on_click_package,
  product,
  flip,
  term,
  noContainer,
  button,
  button_secondary,
  flag,
  footnote,
  price_guidance,
  title,
  display_image,
  display_name,
  display_subtitle,
  simple_bullets,
  bullets_back,
  anchor,
  mt,
  classes,
  price,
  old_price,
  hide_strikethrough,
  promo_code,
  after_promo_copy,
  free_months_with_promo_code,
  show_price_before_0,
  after_price_copy,
  after_price_copy_2,
}: PackageProps) => {
  const {
    options,
    setOptions,
  } = useOrderContext()
  
  const {
      generalOptions,
  } = useGeneralContext()
  
  const { 
    premiseLevelPricingOffer, 
    premiseLevelPricingStartDate, 
    premiseLevelPricingEndDate,
    generalSettingPage
    } = generalOptions

  const [
    open,
    setOpen,
  ] = useState(false)

  const {
    channel, utmSource,
  } = options

  const applyPromoCode = Boolean(
    promo_code &&
    !(utmSource === 'awin' && generalSettingPage?.exclude_aff_from_preapplied_promo),
  )

  const toggleReadMore = (event: React.ChangeEvent<{}>) => {
    event.stopPropagation()
    event.preventDefault()
    setOpen(!open)
  }

  const routerPath = useRouter()
  const isOrderPackagePage = routerPath.asPath.includes('/order/package')
  const client = createCRMApiClient()
  const voiceInBundle = checkIfVoiceExists(product!)
  const priceGuidance = price_guidance || '/month*'

  // Track if we've already reset alternativePhone for this bundle selection
  const [hasResetAlternativePhone, setHasResetAlternativePhone] = useState(false)

  // Reset alternativePhone to true when voice bundle is first selected on package page
  useEffect(() => {
    if (voiceInBundle && selected && isOrderPackagePage && !hasResetAlternativePhone) {
      if (options.alternativePhone === false || options.alternativePhone === undefined) {
        setOptions({
          ...options,
          alternativePhone: true,
        })
      }
      setHasResetAlternativePhone(true)
    }
    
    // Reset the flag when bundle is deselected
    if (!selected) {
      setHasResetAlternativePhone(false)
    }
  }, [voiceInBundle, selected, isOrderPackagePage, hasResetAlternativePhone])

  const [
    hasValidPremiseLevelPricing,
    setHasValidPremiseLevelPricing,
  ] = useState(false)

  const [
    freeMonthsCount,
    setFreeMonthsCount,
  ] = useState<number | null>(null)

  const [
    formattedEndDate,
    setFormattedEndDate,
  ] = useState<string | null>(null)

  // Check for premise level pricing data from both sources:
  // 1. From the generalOptions context (set by SearchBar.tsx)
  // 2. From the Packages.tsx component (via router.query.sprn)
  useEffect(() => {
    // First check if PLPOffer is set to false in session storage
    try {
      const plpOfferValue = sessionStorage.getItem('PLPOffer')
      const shouldExcludeAffiliates = utmSource === 'awin' && generalOptions.generalSettingPage?.exclude_aff_from_plp === true
      if (plpOfferValue === 'false' || shouldExcludeAffiliates) {
        // If PLPOffer is false, reset premise level pricing data
        setHasValidPremiseLevelPricing(false)
        setFreeMonthsCount(null)
        setFormattedEndDate(null)
        return
      }
    } catch (err) {
      // Session storage not supported or error occurred
      console.error('Failed to check PLPOffer in session storage', err)
    }
    
    // If PLPOffer is not false, proceed with normal premise level pricing logic
    // First check if we have premise level pricing data in generalOptions
    if (premiseLevelPricingOffer) {
      const isValid = isPremiseLevelPricingDateValid(premiseLevelPricingStartDate, premiseLevelPricingEndDate)
      setHasValidPremiseLevelPricing(isValid)

      // Extract the number of free months from the offer string (e.g., "EXCLUSIVE_3_MONTHSFREE" -> 3)
      const monthsMatch = premiseLevelPricingOffer.match(/EXCLUSIVE_(\d+)_MONTHSFREE/)
      if (monthsMatch && monthsMatch[1]) {
        setFreeMonthsCount(parseInt(monthsMatch[1], 10))
      }

      // Format the end date for display
      if (premiseLevelPricingEndDate) {
        const endDate = new Date(premiseLevelPricingEndDate)
        const day = endDate.getDate()
        const month = endDate.toLocaleString('en-GB', { month: 'long' })
        setFormattedEndDate(`${day}${getDaySuffix(day)} ${month}`)
      }
      return
    }
    
    // If we don't have premise level pricing data in generalOptions,
    // check if we have a sprn parameter in the URL and fetch the data directly
    const { sprn } = routerPath.query
    if (sprn && !premiseLevelPricingOffer) {
      const fetchPremiseLevelPricing = async () => {
        try {
          const premisePricing = await client.premiseLevelPricing.get(Number(sprn))
          
          if (premisePricing && premisePricing.length > 0) {
            const pricingItem = premisePricing[0]
            
            // Check if the premise level pricing offer is valid
            const isValid = isPremiseLevelPricingDateValid(pricingItem.startdate, pricingItem.enddate)
            
            setHasValidPremiseLevelPricing(isValid)
            
            // Extract the number of free months from the offer string
            const monthsMatch = pricingItem.offer.match(/EXCLUSIVE_(\d+)_MONTHSFREE/)
            if (monthsMatch && monthsMatch[1]) {
              setFreeMonthsCount(parseInt(monthsMatch[1], 10))
            }
            
            // Format the end date for display
            if (pricingItem.enddate) {
              const endDate = new Date(pricingItem.enddate)
              const day = endDate.getDate()
              const month = endDate.toLocaleString('en-GB', { month: 'long' })
              setFormattedEndDate(`${day}${getDaySuffix(day)} ${month}`)
            }
          } else {
            setHasValidPremiseLevelPricing(false)
            setFreeMonthsCount(null)
            setFormattedEndDate(null)
          }
        } catch (error) {
          console.error('Error fetching premise level pricing:', error)
          setHasValidPremiseLevelPricing(false)
          setFreeMonthsCount(null)
          setFormattedEndDate(null)
        }
      }
      
      fetchPremiseLevelPricing()
    } else {
      setHasValidPremiseLevelPricing(false)
      setFreeMonthsCount(null)
      setFormattedEndDate(null)
    }
  }, [premiseLevelPricingOffer, premiseLevelPricingStartDate, premiseLevelPricingEndDate, routerPath.query, utmSource,
  generalOptions.generalSettingPage?.exclude_aff_from_plp,])

  // Helper function to get the day suffix (st, nd, rd, th)
  const getDaySuffix = (day: number): string => {
    if (day > 3 && day < 21) return 'th'
    switch (day % 10) {
      case 1: return 'st'
      case 2: return 'nd'
      case 3: return 'rd'
      default: return 'th'
    }
  }

  async function handleBundleClick(e: React.ChangeEvent<{}>) {
    e.stopPropagation()

  // Only apply premise level pricing if it's valid (within date range)
  // hasValidPremiseLevelPricing is set based on date validation in the useEffect
  const promoCode = hasValidPremiseLevelPricing ? premiseLevelPricingOffer : promo_code
  
  // Double-check dates are valid before applying premise level pricing
  let finalPromoCode = promoCode
  if (promoCode === premiseLevelPricingOffer) {
    const isValid = isPremiseLevelPricingDateValid(premiseLevelPricingStartDate, premiseLevelPricingEndDate)
    // Only use the premise level pricing if dates are valid
    finalPromoCode = isValid ? promoCode : promo_code
  }

    const productWithPromoCode = product ? { 
    ...product, 
    promo_code: finalPromoCode,
    } : product

    const bundleWithVoice = Boolean(product && checkIfVoiceExists(product))
    const bundleWithVoiceOnly = Boolean(product && checkIfVoiceExists(product) && !checkIfWierExists(product) && !checkIfVasExists(product) && !checkIfTVExists(product))

    const bundleObj = {
      product: productWithPromoCode!,
      options,
      client,
      channel,
      bundleWithVoice,
      bundleWithVoiceOnly,
    }

    if (on_click_package) {
      on_click_package(productWithPromoCode, channel)
      return
    }

    if (button?.url) {
      routerPath.push(routerPath.asPath + button?.url, undefined, { shallow: true })
    }

    const bundleOptions = await addToCartOptions(bundleObj)
    setOptions(bundleOptions)
  }

  const flipCard = open ?
    cx(classesOld.flipcard.inner, classesOld.flipcard.cardFlipped) :
    cx(classesOld.flipcard.inner)

  const preconfiguredFlag = () => {
    if (hasValidPremiseLevelPricing && isOrderPackagePage!) {
      return {
        text: "Your exclusive offer",
        position: "center",
        background_color: `${COLORS.orange1}`,
        text_color: `${COLORS.white}`,
        image: <img src={flashIcon} alt="flash sale icon" width="24" height="24" />,
      }
    }

    return null
  }
const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
const animationLineRef = useRef<AnimationLineRef>(null)

const animationLineDomRef = React.useMemo(() => React.createRef<HTMLDivElement>(), [])
const [animationKey, setAnimationKey] = useState(0)

const [isLineVisible] = useIntersectionObserver({
  elementRef: animationLineDomRef,
  horizontalOnly: !isMobile
})

useEffect(() => {
  if (isLineVisible && animationLineRef.current) {
    animationLineRef.current.restart()
    setAnimationKey(prev => prev + 1)
  }
}, [isLineVisible])

  const renderCard = (children: React.ReactNode, side: 'front' | 'back') => {
    return (
      <Box
        className={cx(classes.bundle, classes.commonPackage, side === 'front' ? classesOld.flipcard.front : classesOld.flipcard.back)}
        style={{ 
          borderColor: selected ? COLORS.secondary : (hasValidPremiseLevelPricing && isOrderPackagePage ? COLORS.orange1 : COLORS.gray2),
        }}
        {...BOX}
      >
        <Box>
          <Box>
            {preconfiguredFlag() ?
              <Flag
                {...preconfiguredFlag()}
                position={preconfiguredFlag()?.position as 'left' | 'right' | 'center'}
              /> :
              (flag && flag.flag_text) &&
              <Flag
                text_color={flag?.text_color}
                background_color={flag?.background_color}
                text={flag?.flag_text}
                position={side === 'front' ? 'left' : 'right'}
              />}
            <Box flexGrow={1} className={classes.bundleContent}>
              <Box gridArea="heading" mb={3}>
                {display_name &&
                <TypographyCMS
                  text={display_name}
                  variant="h3"
                  color="primary"
                  margin_bottom={1}
                />}
                {display_subtitle &&
                <TypographyCMS
                  text={display_subtitle}
                  variant="subtitle1"
                  color="primary"
                  lineHeight="1.4"
                />}
              </Box>
              <Box
                gridArea="price"
                mb={3}
              >
                <Price
                  price={applyPromoCode || hasValidPremiseLevelPricing && isOrderPackagePage ? '0' : price}
                  old_price={old_price}
                  price_guidance={
                    hasValidPremiseLevelPricing && isOrderPackagePage && freeMonthsCount 
                      ? ` for ${freeMonthsCount} months` 
                      : applyPromoCode 
                        ? free_months_with_promo_code 
                        : priceGuidance
                  }
                  product={product!}
                  align="left"
                  term={term}
                  discount_full
                  
                  hide_strikethrough={hide_strikethrough || hasValidPremiseLevelPricing && isOrderPackagePage}
                  after_price_copy={
                    (applyPromoCode || hasValidPremiseLevelPricing && isOrderPackagePage) 
                      ? `/month until March ${new Date().getFullYear() + 1}`
                      : (applyPromoCode ? after_promo_copy : undefined)
              }
              showPriceBefore0={show_price_before_0}
                />
                {after_price_copy &&
                  <Typography variant="overline" color="textPrimary" component="div" align="left">
                    {after_price_copy}
                  </Typography>
                }
                {after_price_copy_2 &&
                  <Typography variant="overline" color="textPrimary" component="div" align="left">
                    {after_price_copy_2}
                  </Typography>
                }
                <div
                  ref={animationLineDomRef}
                  style={{ marginTop: 16, minWidth: 250 }}
                >
                  <AnimationLine
                  ref={animationLineRef}
                  key={animationKey}
                  speed={getSpeed(product, 'UPLOAD_SPEED')}
                  startSpeedAnimation={isLineVisible}
                  fileSize="100"
                  />
                </div>
              </Box>
              {simple_bullets &&
              <Box
                gridArea="features"
                pl={{
                  xs: 0,
                  md: 0,
                  lg: 5,
                }}
                mb={{
                  xs: 4,
                  md: 4,
                  lg: 0,
                }}
              >
                {children}
                <Features
                  mt={2}
                  bullet_size="large"
                  text_color="textPrimary"
                  bullet_offer_ends={hasValidPremiseLevelPricing && isOrderPackagePage && formattedEndDate ? {
                    text: `Offer ends ${formattedEndDate}`,
                    icon: orangeFlashIcon
                  } : undefined}
                />
              </Box>}
              <PackageCTA
                product={product}
                button={button}
                button_secondary={button_secondary}
                id={product?.productOfferingId}
                disabled={false}
                selected={selected!}
                handleClick={handleBundleClick}
                handleSecondaryClick={flip ? toggleReadMore : (e: React.ChangeEvent<{}>) => e.preventDefault()}
              />
            </Box>
          </Box>
        </Box>
        {display_image &&
          <Box
            className={classes.displayImage}
            display="flex"
            alignItems="center"
            justifyContent="center"
            minWidth={{
              xs: 0,
              md: 290,
            }}
            overflow="hidden"
            maxHeight={{
              md: 290,
              lg: '100%',
            }}
          >
            <MediaImage
              src={display_image?.src}
              mobile_src={display_image?.mobile_src}
              alt={display_image?.alt}
              width={290}
              height={260}
              full_height
              full_width
              align="top"
              cover
            />
          </Box>}
      </Box>
    )
  }

  const renderCards = () => {
    return (
      <Box id={anchor} className={flipCard}>
        {flip && renderCard(
          <Features
            bullet_size="large"
            simple_bullets={bullets_back}
            text_color="textPrimary"
          />,
          'back',
        )}
        {renderCard(
          <Features
            bullet_size="large"
            simple_bullets={simple_bullets}
            bullets_with_icons={product?.bullets_with_icons}
            text_color="textPrimary"
          />,
          'front',
        )}
      </Box>
    )
  }

  if (noContainer) {
    return (
      <Box component={Wrapper} mt={mt}>
        { renderCards() }
      </Box>
    )
  }

  return (
    <ContainerWithCurve id={anchor} py={10} gradient>
      <Wrapper>
        {title && <TypographyCMS {...createTypographyProps(title)}/>}
        { renderCards() }
        {footnote && (<TypographyCMS {...createTypographyProps(footnote)}/>)}
      </Wrapper>
    </ContainerWithCurve>
  )
})

export default Bundle
